import { BaseRepositoryV2 } from "../base-repository-v2";
import {
  type DeleteDocumentResponse,
  type DocumentsRepositoryMethods,
  type GetDocumentsListResponse,
  type GetRequestDocumentsResponse,
  type GetRequestsCountResponse,
  type GetRequestsDetailResponse,
  type UpdateDocumentPayload,
  type UpdateDocumentResponse,
  type UploadDocumentPayload,
  type UploadDocumentResponse,
  validateFileType,
} from "./types";

export default class DocumentsRepository
  extends BaseRepositoryV2
  implements DocumentsRepositoryMethods
{
  private static instance: DocumentsRepository;
  path: string;

  private constructor() {
    super();
    this.path = "/documents";
  }

  public static getInstance(): DocumentsRepository {
    if (!DocumentsRepository.instance) {
      DocumentsRepository.instance = new DocumentsRepository();
    }
    return DocumentsRepository.instance;
  }

  getDocumentsList = async () => {
    const response = await this.get(`${this.path}/list`, {}, true);
    return this.toAxiosResponse<GetDocumentsListResponse>(response);
  };

  getRequestsCount = async () => {
    const response = await this.get(`${this.path}/requests/count`, {}, true);
    return this.toAxiosResponse<GetRequestsCountResponse>(response);
  };

  getRequestsDetail = async (filters?: { status?: string; q?: string; name?: string }) => {
    const queryParams = new URLSearchParams();

    if (filters?.status) {
      queryParams.append("status", filters.status);
    }
    if (filters?.q) {
      queryParams.append("q", filters.q);
    }
    if (filters?.name) {
      queryParams.append("name", filters.name);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString ? `${this.path}/requests?${queryString}` : `${this.path}/requests`;

    const response = await this.get(endpoint, {}, true);
    const axiosResponse = await this.toAxiosResponse<{ data: GetRequestsDetailResponse }>(response);

    return {
      ...axiosResponse,
      data: axiosResponse.data.data,
    };
  };

  getRequestDocuments = async (processId: string) => {
    const response = await this.get(`${this.path}/requests/${processId}/documents`, {}, true);
    const axiosResponse = await this.toAxiosResponse<{ data: GetRequestDocumentsResponse }>(
      response
    );
    // Extract the array from the nested data object
    return {
      ...axiosResponse,
      data: axiosResponse.data.data,
    };
  };

  deleteDocument = async (documentId: string) => {
    const response = await this.delete(`${this.path}/${documentId}`, {}, true);
    return this.toAxiosResponse<DeleteDocumentResponse>(response);
  };

  uploadDocument = async (payload: UploadDocumentPayload) => {
    // Validate file type before uploading
    if (payload.file) {
      validateFileType(payload.file as File);
    }

    const formData = new FormData();

    // Check if this is a document request upload (has requiredDocumentId)
    if (payload.requiredDocumentId) {
      // Document request upload format
      // According to the API, we don't send processId and requiredDocumentId as separate fields
      // Everything goes in the metadata array

      if (payload.file && payload.processId) {
        const file = payload.file as File;
        const fileName = file.name;
        const contentType = file.type || "application/octet-stream";

        // Build metadata array with fileName, contentType, processId, and requiredDocumentId
        const metadataArray = [
          {
            fileName,
            contentType,
            processId: payload.processId,
            requiredDocumentId: payload.requiredDocumentId,
            stageId: payload.stageId,
          },
        ];

        // Use file_0 as field name (same as standard upload)
        formData.append("file_0", payload.file);
        formData.append("metadata", JSON.stringify(metadataArray));
      } else if (payload.documentId && payload.processId) {
        // When using a preloaded document for a process
        const metadataArray = [
          {
            predefinedDocumentId: payload.documentId,
            fileName: payload.fileName,
            processId: payload.processId,
            requiredDocumentId: payload.requiredDocumentId,
            stageId: payload.stageId,
          },
        ];
        formData.append("metadata", JSON.stringify(metadataArray));
      }
    } else {
      // Legacy/standard upload format
      const file = payload.file as File;
      const fileName = file.name;
      const contentType = file.type || "application/octet-stream";

      // Build metadata array with fileName, contentType, and documentType
      const metadataArray = [
        { fileName, contentType },
        ...Object.entries(payload.metadata || {}).map(([key, value]) => ({ key, value })),
      ];

      if (payload.file) {
        formData.append("file_0", payload.file);
      }
      formData.append("metadata", JSON.stringify(metadataArray));
      if (payload.processId) {
        formData.append("processId", payload.processId);
      }
    }

    const response = await this.customCall(
      `${this.path}/upload`,
      {
        method: "POST",
        body: formData,
      },
      {},
      true
    );
    return this.toAxiosResponse<UploadDocumentResponse>(response);
  };

  updateDocument = async (documentId: string, payload: UpdateDocumentPayload) => {
    // Validate file type before updating
    if (payload.file instanceof File || payload.file instanceof Blob) {
      validateFileType(payload.file);
    }

    const formData = new FormData();

    const file = payload.file as File;
    const fileName = file.name;
    const contentType = file.type || "application/octet-stream";

    const metadataArray = [{ fileName, contentType }];

    formData.append("file_0", payload.file);
    formData.append("metadata", JSON.stringify(metadataArray));

    const response = await this.customCall(
      `${this.path}/${documentId}`,
      {
        method: "PUT",
        body: formData,
      },
      {},
      true
    );
    return this.toAxiosResponse<UpdateDocumentResponse>(response);
  };
}
